import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { invoicesAPI } from '../../api';
import { stripIframePreviewScripts } from '../../lib/stripIframePreviewScripts';
import Layout from '../../components/Layout';
import InvoicePaymentForm from '../../components/invoices/InvoicePaymentForm';
import { Button } from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { Pencil, Printer, CirclePoundSterling, Copy, ArrowLeft, Mail } from 'lucide-react';

export default function InvoiceView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const iframeRef = useRef(null);

  const [html, setHtml] = useState('');
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [duplicating, setDuplicating] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const [htmlDoc, inv] = await Promise.all([
        invoicesAPI.getHtml(id, { embed: true }),
        invoicesAPI.getOne(id),
      ]);
      setHtml(stripIframePreviewScripts(typeof htmlDoc === 'string' ? htmlDoc : ''));
      setInvoice(inv);
    } catch (e) {
      setError(e.message || 'Failed to load invoice');
      setHtml('');
      setInvoice(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePrint = () => {
    const win = iframeRef.current?.contentWindow;
    if (win) {
      win.focus();
      win.print();
    }
  };

  const handleDuplicate = async () => {
    if (!id || duplicating) return;
    try {
      setDuplicating(true);
      setError('');
      const inv = await invoicesAPI.getOne(id);
      if (!inv?.client?.id) {
        throw new Error('Invoice has no client; cannot duplicate');
      }
      const { invoiceNum } = await invoicesAPI.getNextNumber();
      const payload = {
        clientId: inv.client.id,
        invoiceNum,
        reference: inv.reference ? `Copy of ${inv.reference}` : null,
        dateCreated: new Date().toISOString().split('T')[0],
        dateIssued: null,
        dueDate: null,
        vatRate: inv.vatRate ?? 20,
        notes: inv.notes || null,
        terms: inv.terms || null,
        materials: (inv.materials || []).map((m) => ({
          description: m.description,
          quantity: parseFloat(m.quantity) || 0,
          unitPrice: parseFloat(m.unitPrice) || 0,
          partNumber: m.partNumber,
          notes: m.notes,
          costPrice: parseFloat(m.costPrice) || 0,
          markup: parseFloat(m.markup) || 0,
        })),
        labour: (inv.labour || []).map((l) => ({
          description: l.description,
          hours: parseFloat(l.hours) || 0,
          hourlyRate: parseFloat(l.hourlyRate) || 0,
          notes: l.notes,
        })),
      };
      const response = await invoicesAPI.create(payload);
      const row = response?.invoice || response;
      const newId = row?.id;
      if (!newId) throw new Error('Duplicate failed: no invoice id returned');
      navigate(`/tenant/invoices/${newId}`);
    } catch (e) {
      setError(e.message || 'Could not duplicate invoice');
      console.error(e);
    } finally {
      setDuplicating(false);
    }
  };

  const balanceDue = invoice?.balanceDue ?? 0;
  const canPay = balanceDue > 0 && !invoice?.isDeleted;

  return (
    <Layout>
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <h1 className="text-display text-[var(--ds-text-primary)] font-bold truncate">
                {invoice?.invoiceNum ? `Invoice ${invoice.invoiceNum}` : 'Invoice'}
              </h1>
              <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
                Read-only preview (same layout as client print view)
              </p>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/tenant/invoices/${id}`)}
                className="border-[var(--ds-control-field-border)] bg-[var(--ds-control-field-bg)] text-[var(--ds-text-primary)]"
              >
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/tenant/invoices/${id}/send`)}
                disabled={loading || !id || invoice?.isDeleted}
                className="border-[var(--ds-control-field-border)] bg-[var(--ds-control-field-bg)] text-[var(--ds-text-primary)] disabled:opacity-50"
              >
                <Mail className="h-4 w-4 mr-2" />
                Send
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrint}
                disabled={!html || loading}
                className="border-[var(--ds-control-field-border)] bg-[var(--ds-control-field-bg)] text-[var(--ds-text-primary)]"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPaymentOpen(true)}
                disabled={!canPay || loading}
                className="border-[var(--ds-control-field-border)] bg-[var(--ds-control-field-bg)] text-[var(--ds-text-primary)] disabled:opacity-50"
              >
                <CirclePoundSterling className="h-4 w-4 mr-2" />
                Log payment
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDuplicate}
                disabled={duplicating || loading || !invoice}
                className="border-[var(--ds-control-field-border)] bg-[var(--ds-control-field-bg)] text-[var(--ds-text-primary)] disabled:opacity-50"
              >
                <Copy className="h-4 w-4 mr-2" />
                {duplicating ? 'Duplicating…' : 'Duplicate'}
              </Button>
              <Button variant="primary" size="sm" onClick={() => navigate('/tenant/invoices')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Close
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {error && (
          <div className="mb-4 p-4 bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 rounded-[var(--ds-radius-md)]">
            <p className="text-xs text-[var(--ds-status-danger)]">{error}</p>
          </div>
        )}

        <Card className="overflow-hidden bg-[var(--ds-surface-panel)] border-[var(--ds-border-subtle)]">
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-16">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--ds-accent-primary)]" />
                <p className="mt-2 text-xs text-[var(--ds-text-muted)]">Loading preview…</p>
              </div>
            ) : html ? (
              <iframe
                ref={iframeRef}
                title={`Invoice ${invoice?.invoiceNum || ''}`}
                srcDoc={html}
                sandbox="allow-same-origin allow-modals"
                className="w-full min-h-[85vh] border-0 bg-white block"
              />
            ) : (
              <p className="p-8 text-sm text-[var(--ds-text-muted)] text-center">No preview available.</p>
            )}
          </CardContent>
        </Card>
      </div>

      {paymentOpen && invoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-3xl w-full p-6 shadow-xl border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Log payment</h2>
              <button
                type="button"
                onClick={() => setPaymentOpen(false)}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 text-2xl transition-colors leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <InvoicePaymentForm
              invoiceId={invoice.id}
              balanceDue={balanceDue}
              onSuccess={async () => {
                setPaymentOpen(false);
                await load();
              }}
              onCancel={() => setPaymentOpen(false)}
            />
          </div>
        </div>
      )}
    </Layout>
  );
}
